import { TGetWishlistSuggestions } from '@daymade/api-contracts';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { TWishlistPrize } from 'stores/UserStore';
import { prizeApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import AboutUsSiteInnerContainer from '../../about-us/AboutUsSiteInnerContainer';
import DashboardComponentHeader from '../common/DashboardComponentHeader';
import DashboardComponentLink from '../common/DashboardComponentLink';
import EmptyPrize from './EmptyPrize';
import SuggestedPrize from './SuggestedPrize';
import UnfavouritePrizeModal from './UnfavouritePrizeModal';
import WishlistPrize from './WishlistPrize';
import styles from './YourWishlist.module.css';

const YourWishlist = observer(() => {
  const router = useRouter();
  const { wishListPrizes, initialized, fetchWishListPrizes } = useUserStore();
  const [fullWishlist, setFullWishlist] = useState(wishListPrizes);
  const [suggestedPrizes, setSuggestedPrizes] =
    useState<TGetWishlistSuggestions>();
  const [shortlistSuggestedPrizes, setShortlistSuggestedPrizes] =
    useState<TGetWishlistSuggestions>();
  const [selectedPrize, setSelectedPrize] = useState<TWishlistPrize>();

  useEffect(() => {
    const getSuggestedPrizes = async () => {
      const suggestions = await prizeApiClient.auth.getWishlistSuggestions({
        query: { limit: 2 },
      });

      if (suggestions.status === HttpStatusCode.OK) {
        setSuggestedPrizes(suggestions.body);
      }
    };

    getSuggestedPrizes();
  }, []);

  useEffect(() => {
    if (suggestedPrizes && wishListPrizes) {
      const idsInWishlist = new Set(wishListPrizes.map((p) => p.id));

      const filteredSuggestions = suggestedPrizes.filter(
        (prize) => !idsInWishlist.has(prize.id),
      );
      setShortlistSuggestedPrizes(filteredSuggestions);
    }

    if (initialized && wishListPrizes) {
      setFullWishlist(wishListPrizes);
    }
  }, [suggestedPrizes, wishListPrizes, initialized]);

  const openUnfavouritePrizeModal = (prize: TWishlistPrize) => {
    setSelectedPrize(prize);
  };

  const unfavouritePrize = async (prize: TWishlistPrize) => {
    const response = await prizeApiClient.auth.unfavouritePrize({
      body: {
        prizeId: prize.id,
      },
    });
    closeModal();

    if (response.status === HttpStatusCode.OK) {
      setFullWishlist((prevState) => {
        const shortenedWishlist = prevState.filter((p) => p.id !== prize.id);
        return shortenedWishlist;
      });
      fetchWishListPrizes();
    } else {
      throw new Error(
        'Currently unable to remove wishlist prize. Try again later.',
      );
    }
  };

  const favouritePrize = async (prize: TGetWishlistSuggestions[number]) => {
    const response = await prizeApiClient.auth.favouritePrize({
      body: {
        prizeId: prize.id,
      },
    });

    if (response.status === HttpStatusCode.OK) {
      fetchWishListPrizes();
    } else {
      throw new Error(
        'Currently unable to add wishlist prize. Try again later.',
      );
    }
  };

  const closeModal = () => {
    setSelectedPrize(undefined);
  }

  return (
    <AboutUsSiteInnerContainer background="white" borderRadius withPadding>
      {selectedPrize && (
        <UnfavouritePrizeModal
          prize={selectedPrize}
          handleOnClose={() => closeModal()}
          unfavouritePrize={(prize) => unfavouritePrize(prize)}
        />
      )}
      <div className={styles.container}>
        <DashboardComponentHeader
          title="Your wishlist"
          subtitle="The prizes you're dreaming of all in one place"
          separator
        />
        <div className={styles.content}>
          <div className={styles.prizes}>
            {fullWishlist?.length > 0
              ? fullWishlist
                  ?.slice(0, 3)
                  .map((prize, i) => (
                    <WishlistPrize
                      key={`${i}-wishlist-item`}
                      prize={prize}
                      favourited
                      unfavouritePrize={() => openUnfavouritePrizeModal(prize)}
                    />
                  ))
              : null}

            {fullWishlist?.length < 3 && (
              <EmptyPrize key={`empty-item`} router={router} />
            )}

            {shortlistSuggestedPrizes &&
            shortlistSuggestedPrizes?.length > 0 ? (
              <>
                {fullWishlist?.length == 0 &&
                  shortlistSuggestedPrizes?.map((prize, i) => (
                    <SuggestedPrize
                      key={`${i}-suggested-item`}
                      prize={prize}
                      favouritePrize={() => favouritePrize(prize)}
                    />
                  ))}
                {fullWishlist?.length == 1 && (
                  <SuggestedPrize
                    prize={shortlistSuggestedPrizes[0]}
                    favouritePrize={() =>
                      favouritePrize(shortlistSuggestedPrizes[0])
                    }
                  />
                )}
              </>
            ) : null}
          </div>
          <DashboardComponentLink
            linkText={fullWishlist.length > 3 ? 'View all' : 'View more prizes'}
            href={fullWishlist.length > 3 ? '/account/wish-list' : '/prizes'}
          />
        </div>
      </div>
    </AboutUsSiteInnerContainer>
  );
});

export default YourWishlist;
